import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { CircularProgress, Alert } from '@mui/material'
import { getAllPosts } from '../../api/posts'
import Pagination from '../Paginations'

const PostsList = () => {
  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const navigate = useNavigate()

  // Fetch posts with filters - only published posts for users
  const { data: postsData, isLoading, error } = useQuery({
    queryKey: ['posts-list', currentPage, searchTerm],
    queryFn: () => getAllPosts({
      page: currentPage,
      limit: 12,
      status: 'published',
      search: searchTerm,
      sortBy: 'created_at',
      sortOrder: 'desc'
    })
  })

  const posts = postsData?.data?.posts || []
  const totalPages = postsData?.data?.pagination?.totalPages || 0
  const totalItems = postsData?.data?.pagination?.totalItems || 0

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const stripHtml = (html) => {
    const div = document.createElement('div')
    div.innerHTML = html
    return div.textContent || div.innerText || ''
  }

  const handleSearch = (e) => {
    e.preventDefault()
    setCurrentPage(1) // Reset to first page on search
  }

  const getPostExcerpt = (post) => {
    if (post.excerpt) {
      return post.excerpt
    }
    const content = stripHtml(post.content)
    return content.length > 150 ? content.substring(0, 150) + '...' : content
  }

  return (
    <div className="posts-list-container">
      {/* Header */}
      <div className="posts-header">
        <div className="header-content">
          <div className="header-badge">
            <i className="fi fi-rr-newspaper"></i>
            <span>Blog & Updates</span>
          </div>
          <h1 className="page-title">Latest Posts</h1>
          <p className="page-subtitle">
            Discover the latest updates, insights, and news from our team
          </p>
        </div>
        
        <button 
          className="back-button"
          onClick={() => navigate(-1)}
        >
          <i className="fi fi-rr-arrow-left"></i>
          <span>Back to Dashboard</span>
        </button>
      </div>

      {/* Search */}
      <div className="search-section">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-group">
            <i className="fi fi-rr-search search-icon"></i>
            <input
              type="text"
              placeholder="Search posts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="search-button">
              Search
            </button>
          </div>
        </form>
      </div>

              

      {/* Loading State */}
      {isLoading && (
        <div className="loading-container">
          <CircularProgress size={50} />
          <p>Loading posts...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" className="error-alert">
          {error.message || 'Failed to load posts'}
        </Alert>
      )}

      {/* Posts Grid */}
      {!isLoading && !error && (
        <>
          {posts.length === 0 ? (
            <div className="empty-state">
              <i className="fi fi-rr-document empty-icon"></i>
              <h3>No posts found</h3>
              <p>
                {searchTerm 
                  ? `No posts match your search "${searchTerm}"`
                  : 'No posts available for the selected filter'
                }
              </p>
              {searchTerm && (
                <button 
                  className="clear-search-button"
                  onClick={() => {
                    setSearchTerm('')
                    setCurrentPage(1)
                  }}
                >
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <div className="posts-grid">
              {posts.map((post) => (
                <article key={post.id} className="post-card">
                  <Link to={`/dashboard/posts/${post.slug || post.id}`} className="post-link">
                    {/* Featured Image */}
                    {post.featured_image && (
                      <div className="post-image">
                        <img src={post.featured_image} alt={post.title} />
                      </div>
                    )}

                    {/* Post Content */}
                    <div className="post-content">
                      {/* Title */}
                      <h2 className="post-title">{post.title}</h2>

                      {/* Excerpt */}
                      <p className="post-excerpt">
                        {getPostExcerpt(post)}
                      </p>

                      {/* Tags */}
                      {post.tags && post.tags.length > 0 && (
                        <div className="post-tags">
                          {post.tags.slice(0, 3).map((tag, index) => (
                            <span key={index} className="tag">
                              {tag}
                            </span>
                          ))}
                          {post.tags.length > 3 && (
                            <span className="tag more-tags">
                              +{post.tags.length - 3} more
                            </span>
                          )}
                        </div>
                      )}

                      {/* Meta */}
                      <div className="post-meta">
                        <div className="author-info">
                          <span className="author">
                            {post.author?.first_name} {post.author?.last_name}
                          </span>
                          <span className="date">{formatDate(post.created_at)}</span>
                        </div>
                        
                        <div className="post-stats">
                          <span className="stat">
                            <i className="fi fi-rr-eye"></i>
                            {post.views_count || 0}
                          </span>
                          <span className="stat">
                            <i className="fi fi-rr-heart"></i>
                            {post.likes_count || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-container">
              <Pagination 
                currentPage={currentPage}
                totalPages={totalPages}
                handlePageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}

      <style jsx>{`
        .posts-list-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 24px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: #f9fafb;
          min-height: 100vh;
        }

        .posts-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
      
          padding: 32px 0;
        }

        .header-content {
          flex: 1;
        }

        .header-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(to right, #eff6ff, #dbeafe);
          color: #2563eb;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;

        }

        .page-title {
          font-size: 3rem;
          font-weight: 800;
          color: #1f2937;
          margin: 0 0 12px 0;
          background: linear-gradient(to right, #1f2937, #374151);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .page-subtitle {
          color: #6b7280;
          font-size: 1.125rem;
          margin: 0;
          line-height: 1.6;
        }

        .back-button {
          display: flex;
          align-items: center;
          gap: 8px;
          background: transparent;
          border: none;
          padding: 12px 20px;
          color: #475569;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          margin-left: 20px;
        }

        .back-button:hover {
          color: #1f2937;
        }

        .search-section {
          padding: 24px 0;
          
        }

        .search-form {
          width: 100%;
          max-width: 500px;
         
        }

        .search-input-group {
          display: flex;
          align-items: center;
          background: transparent;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          overflow: hidden;
          transition: all 0.2s;
        }

        .search-input-group:focus-within {
          border-color: #3b82f6;
        }

        .search-icon {
          padding: 0 16px;
          color: #6b7280;
        }

        .search-input {
          flex: 1;
          padding: 12px 0;
          border: none;
          outline: none;
          font-size: 0.9rem;
          background: transparent;
        }

        .search-button {
          background: #3b82f6;
          border: none;
          padding: 12px 24px;
          color: white;
          cursor: pointer;
          transition: all 0.2s;
          font-weight: 500;
        }

        .search-button:hover {
          background: #2563eb;
        }





        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 100px 0;
          gap: 20px;
        }

        .error-alert {
          margin-bottom: 20px;
        }

        .empty-state {
          text-align: center;
          padding: 80px 20px;
          color: #666;
        }

        .empty-icon {
          font-size: 4rem;
          color: #ddd;
          margin-bottom: 20px;
        }

        .empty-state h3 {
          margin: 0 0 10px 0;
          color: #333;
        }

        .clear-search-button {
          background: #4A90E2;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          cursor: pointer;
          margin-top: 15px;
          transition: background 0.2s;
        }

        .clear-search-button:hover {
          background: #357abd;
        }

        .posts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
          gap: 32px;
          margin-bottom: 48px;
        }

        .post-card {
          background: transparent;
          overflow: hidden;
          transition: all 0.2s ease;
        }

        .post-card:hover {
          transform: translateY(-2px);
        }

        .post-link {
          text-decoration: none;
          color: inherit;
          display: block;
        }

        .post-image {
          height: 240px;
          overflow: hidden;
          position: relative;
        }

        .post-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .post-card:hover .post-image img {
          transform: scale(1.1);
        }

        .post-content {
          padding: 32px;
        }



        .post-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0 0 16px 0;
          line-height: 1.3;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .post-excerpt {
          color: #6b7280;
          font-size: 0.95rem;
          line-height: 1.6;
          margin: 0 0 20px 0;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .post-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 20px;
        }

        .tag {
          background: #3b82f6;
          color: white;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .more-tags {
          background: #6b7280;
        }

        .post-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 20px;
          border-top: 1px solid #f1f5f9;
        }

        .author-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .author {
          font-weight: 600;
          color: #1f2937;
          font-size: 0.9rem;
        }

        .date {
          color: #9ca3af;
          font-size: 0.8rem;
        }

        .post-stats {
          display: flex;
          gap: 16px;
        }

        .stat {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #6b7280;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .pagination-container {
          display: flex;
          justify-content: center;
          margin-top: 40px;
        }

        @media (max-width: 768px) {
          .posts-list-container {
            padding: 15px;
          }

          .page-title {
            font-size: 2rem;
          }

          .posts-header {
            flex-direction: column;
            gap: 20px;
          }

          .back-button {
            margin-left: 0;
            align-self: flex-start;
          }

          .search-section {
            padding: 16px 0;
          }

          .search-form {
            max-width: none;
          }

          .posts-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .post-meta {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }
        }
      `}</style>
    </div>
  )
}

export default PostsList 